/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/pages/CompleteProfilePage.tsx
 * Release: v2.0.0 - Pagina completamento profilo obbligatorio
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import AppLayout from '../components/templates/AppLayout';
import AdminProfileSimple from './AdminProfileSimple';
import { CustomerRegistrationForm } from '../components/modular-form/CustomerRegistrationForm';
import { CheckCircle, X } from 'lucide-react';



// Componente principale con routing
const CompleteProfilePage: React.FC = () => {
  // Controlla modalità PRIMA di qualsiasi hook
  const urlParams = new URLSearchParams(window.location.search);
  const isAdminMode = urlParams.get('admin') === 'true';
  const targetUserId = urlParams.get('userId');
  
  // Se admin mode, usa componente ultra-semplificato
  if (isAdminMode && targetUserId) {
    return <AdminProfileSimple />;
  }
  
  // Solo per utenti normali: usa hook
  const { user, getToken } = useAuth();
  const [formType] = useState<'azienda' | 'privato'>('azienda');
  const [initialData, setInitialData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Determina da dove arriva l'utente e se è forzato
  const fromLogin = urlParams.get('from') === 'login';
  const fromGoogle = urlParams.get('from') === 'google';
  const isForced = user?.needs_profile_completion || user?.status === 'pending_completion';
  
  // Carica i dati del profilo utente - STESSA LOGICA DEL SUPERADMIN
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) {
        setIsLoadingData(false);
        return;
      }

      try {
        const token = getToken?.();
        if (!token) {
          setIsLoadingData(false);
          return;
        }

        // Usa l'endpoint appropriato per l'utente corrente (non SuperAdmin)
        const response = await fetch(`/api/profiles/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-user-id': user.id
          }
        });

        if (response.ok) {
          const profileData = await response.json();
          console.log('📊 [CompleteProfilePage] Dati profilo caricati (API Utente):', profileData);
          setInitialData(profileData.profile || null);
        } else {
          console.log('📊 [CompleteProfilePage] Nessun profilo esistente, user nuovo');
          setInitialData(null);
        }
      } catch (error) {
        console.error('❌ [CompleteProfilePage] Errore caricamento profilo:', error);
        setInitialData(null);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadUserProfile();
  }, [user?.id, getToken]);

  // Messaggio dinamico basato sulla provenienza
  const getMessage = () => {
    if (fromLogin && isForced) {
      return "🔄 Per completare l'accesso, devi prima completare il tuo profilo cliente.";
    } else if (fromGoogle && isForced) {
      return "🔄 Accesso Google effettuato! Completa ora il tuo profilo per continuare.";
    } else if (isForced) {
      return "⚠️ Il tuo profilo è incompleto. Completa ora per accedere a tutti i servizi.";
    } else {
      return "📝 Modifica qui i dati del tuo profilo cliente.";
    }
  };

  // Stato per il popup di conferma salvataggio
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Layout normale con AppLayout per utenti
  return (
    <AppLayout>
      {/* Banner dinamico basato su provenienza e stato */}
      <div className={`text-white p-4 mb-6 rounded-lg shadow-lg ${
        isForced 
          ? 'bg-gradient-to-r from-orange-600 to-red-600' 
          : 'bg-gradient-to-r from-blue-600 to-purple-600'
      }`}>
        <div className="flex items-center space-x-3">
          <span className="text-2xl">
            {fromGoogle ? '🔗' : fromLogin ? '🔄' : isForced ? '⚠️' : '👤'}
          </span>
          <div>
            <h2 className="font-bold text-lg">
              {isForced ? 'Completamento Profilo Richiesto' : 'Gestione Profilo Cliente'}
            </h2>
            <p className={`text-sm ${isForced ? 'text-orange-100' : 'text-blue-100'}`}>
              {getMessage()}
            </p>
          </div>
        </div>
      </div>

      {/* Form di completamento profilo reale */}
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">
            Completa il tuo Profilo Cliente
          </h1>
          <p className="text-slate-300 text-lg">
            Per accedere a tutti i servizi Coronatek, completa i dati del tuo profilo aziendale o personale.
          </p>
        </div>

        {/* Form cliente reale */}
        {/* Il simulatore è disponibile come pulsante floating in basso a sinistra (sistema globale) */}
        <div className="bg-slate-800 rounded-lg p-6">
          {isLoadingData ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-300">Caricamento profilo...</p>
            </div>
          ) : (
            <CustomerRegistrationForm
              userId={user?.id || ""}
              initialData={initialData}
              onSuccess={(message) => {
                console.log('Profilo salvato:', message);
                // Mostra popup di conferma
                setSuccessMessage(message || 'Profilo salvato con successo!');
                setShowSuccessPopup(true);
              }}
              onError={(error) => {
                console.error('Errore salvataggio:', error);
                // Qui puoi gestire gli errori
              }}
              showTypeSwitch={true}
              defaultType={formType}
            />
          )}
        </div>
      </div>

      {/* Popup di conferma salvataggio */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-green-500/30 animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Salvataggio Completato</h3>
              </div>
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="p-1 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <p className="text-slate-300 text-lg mb-2">{successMessage}</p>
                <p className="text-slate-400 text-sm">
                  I tuoi dati sono stati salvati correttamente nel sistema.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-center">
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors"
              >
                OK, ho capito
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CompleteProfilePage;
